import AudioPlayer from '@/components/AudioPlayer/AudioPlayer';
import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { audiosDns } from '@/environment';
import dump from '@/images/dump.svg';
import loading from '@/images/loader.json';
import Upload from '@/images/uploadIcon.svg';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { formatS3Url } from '@/utils/s3';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { PromptContainer } from './common';
import dashborder from './images/dashborder.svg';

const UploadContainer = styled.div`
    height: 180px;
    width: 100%;
    margin-top: 32px;
    margin-bottom: 16px;
`;

const UploadBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100%;
    padding: 32px;

    border-radius: 12px;
    background-image: url(${dashborder});
    background-size: cover;
    cursor: pointer;

    &:focus {
        outline: none;
    }

    transition: background-color 0.2s;

    &:hover {
        background-color: ${({ theme }) => theme.colors.black[50]};
        transition: background-color 0.2s;
    }
`;

const UploadTitle = styled(Typography)`
    margin-top: 14px;
    margin-bottom: 6px;
`;

const UploadInstructions = styled(Typography)`
    text-align: center;
    line-height: normal;
    color: ${({ theme }) => theme.colors.black[600]};
`;

const FileContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    gap: 12px;
`;

const FileTitle = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const DeleteButton = styled(IconButton)`
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    height: 30px;
    width: 30px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        opacity: 0.4;
    }
`;

const PlayerContainer = styled(PromptContainer)`
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const LoaderContainer = styled.div`
    height: 100%;
    width: 100%;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
`;

const LoadingAnimation = styled(Player)`
    height: 60px;
    width: 60px;
`;

interface Props {
    onFileSelect: (file: File) => Promise<[string, number, string]>;
    audioData: AudioData | null;
    onAudioUploaded: (audioData: AudioData) => void;
    onAudioUploadError?: () => void;
    onDelete?: () => void;
}

export const VoiceUploadBox = ({ onFileSelect, audioData, onAudioUploaded, onAudioUploadError, onDelete }: Props) => {
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const theme = useTheme();

    const handleDrop = async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];

        if (!file) {
            return;
        }

        setIsUploading(true);
        setUploadFile(file);

        try {
            const [data, duration, url] = await onFileSelect(file);

            if (!data) {
                handleError();
            } else {
                const uri = formatS3Url(audiosDns, url);
                onAudioUploaded({ data, listenUrl: uri, duration });
            }
        } catch (error) {
            handleError();
        }

        setIsUploading(false);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: { 'audio/*': [], 'video/*': [] },
        multiple: false,
    });

    const handleError = () => {
        setUploadFile(null);
        setIsUploading(false);
        onAudioUploadError?.();
    };

    const handleDelete = () => {
        setUploadFile(null);
        onDelete?.();
    };

    if (uploadFile) {
        return (
            <UploadContainer>
                <FileContainer>
                    <FileTitle>
                        <Typography fontWeight={500} variant="body1" lineHeight="normal">
                            {uploadFile.name}
                        </Typography>
                        <DeleteButton disableRipple ariaLabel="delete audio" onClick={handleDelete} disabled={isUploading}>
                            <MaskedIcon src={dump} size="16px" color={theme.colors.error.main} />
                        </DeleteButton>
                    </FileTitle>
                    <PlayerContainer>
                        {isUploading || !audioData ? (
                            <LoaderContainer>
                                <LoadingAnimation src={loading} autoplay loop />
                                <Typography variant="body2" color={theme.colors.secondary[500]} fontWeight={500}>
                                    Uploading...
                                </Typography>
                            </LoaderContainer>
                        ) : (
                            <AudioPlayer audioData={audioData} />
                        )}
                    </PlayerContainer>
                </FileContainer>
            </UploadContainer>
        );
    }

    return (
        <UploadContainer>
            <UploadBox {...getRootProps()}>
                <input data-testid="clone-voice-upload-audio-input" {...getInputProps()} />
                <MaskedIcon src={Upload} size="31px" color={theme.colors.secondary[500]} />
                <UploadTitle variant="h3">Upload file</UploadTitle>
                <UploadInstructions variant="body2">
                    Minimum 30 seconds, up to 5 minutes long.
                    <br />
                    Click or drag file here. Video or Audio, up to 10mb.
                </UploadInstructions>
            </UploadBox>
        </UploadContainer>
    );
};
